import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
    X,
    UserRound,
    Phone,
    Mail,
    CalendarDays,
    BookOpen,
    AlertTriangle
} from 'lucide-react';

import styles from './AddCustomerForm.module.css';
import { useStore } from '../../context/StoreContext';
import { SMIIFY_API } from '../../config/api';

// Backend address comes from src/config/api.js (never hard-coded)
const CUSTOMERS_API = `${SMIIFY_API}/customers`;

const AddCustomerForm = ({ onClose, onSuccess, initialPhone = '' }) => {
    const token = localStorage.getItem('smiify_token');
    const { activeStore } = useStore();

    const [formData, setFormData] = useState({
        title: 'Mr.',
        firstName: '',
        lastName: '',
        phone: String(initialPhone || '').replace(/\D/g, '').slice(0, 10),
        email: '',
        gender: 'Male',
        dob: '',
        anniversary: '',
        notes: ''
    });

    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = previousOverflow;
        };
    }, []);

    useEffect(() => {
        const handleEscape = (event) => {
            if (event.key === 'Escape' && !isSaving) onClose();
        };
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [isSaving, onClose]);

    const handleChange = (event) => {
        const { name, value } = event.target;
        const nextValue = name === 'phone' ? value.replace(/\D/g, '').slice(0, 10) : value;
        setFormData((previous) => ({ ...previous, [name]: nextValue }));
        setError('');
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        const firstName = formData.firstName.trim();
        const lastName = formData.lastName.trim();
        const phone = formData.phone.trim();
        const storeId = activeStore?._id || activeStore?.id;

        if (!firstName || !phone) {
            setError('First Name and Mobile Number are required.');
            return;
        }
        if (!/^\d{10}$/.test(phone)) {
            setError('Mobile Number must be exactly 10 digits.');
            return;
        }
        if (!storeId || storeId === 'global_hq') {
            setError('Please select a branch before adding a customer.');
            return;
        }

        const payload = {
            name: [formData.title, firstName, lastName].filter(Boolean).join(' '),
            phone,
            email: formData.email.trim(),
            gender: formData.gender,
            notes: formData.notes.trim(),
            storeId: String(storeId),
        };
        if (formData.dob) payload.dob = formData.dob;
        if (formData.anniversary) payload.anniversary = formData.anniversary;

        setIsSaving(true);
        setError('');

        try {
            const response = await fetch(CUSTOMERS_API, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                    'x-store-id': String(storeId)
                },
                body: JSON.stringify(payload)
            });

            const data = await response.json().catch(() => ({}));

            if (response.ok && data.success) {
                onSuccess(data.data);
            } else {
                setError(data.message || 'Failed to add customer. This mobile number may already exist.');
            }
        } catch (submitError) {
            setError('Cannot reach the server. Check your internet and try again.');
        } finally {
            setIsSaving(false);
        }
    };

    const handleOverlayMouseDown = (event) => {
        if (event.target === event.currentTarget && !isSaving) onClose();
    };

    if (typeof document === 'undefined') return null;

    return createPortal(
        <div className={styles.drawerOverlay} onMouseDown={handleOverlayMouseDown}>
            <section
                className={styles.drawerContent}
                role="dialog"
                aria-modal="true"
                aria-labelledby="add-customer-title"
                onMouseDown={(event) => event.stopPropagation()}
            >
                <div className={styles.drawerHeader}>
                    <div className={styles.headerTitle}>
                        <div className={styles.iconBox}>
                            <UserRound size={18} strokeWidth={2.4} />
                        </div>
                        <div>
                            <h3 id="add-customer-title">Add New Customer</h3>
                            <p>Create a complete customer profile</p>
                        </div>
                    </div>

                    <button type="button" onClick={onClose} className={styles.closeBtn} aria-label="Close" disabled={isSaving}>
                        <X size={18} strokeWidth={2.4} />
                    </button>
                </div>

                <form id="addCustomerForm" onSubmit={handleSubmit} className={styles.drawerBody} autoComplete="off">
                    {error && (
                        <div className={styles.errorBanner} role="alert">
                            <AlertTriangle size={15} />
                            <span>{error}</span>
                        </div>
                    )}

                    <div className={styles.sectionTitle}>Basic Details</div>

                    <div className={styles.nameRow}>
                        <div className={`${styles.inputGroup} ${styles.titleGroup}`}>
                            <label htmlFor="customer-title">Title</label>
                            <select id="customer-title" name="title" value={formData.title} onChange={handleChange} className={styles.input}>
                                <option value="Mr.">Mr.</option>
                                <option value="Ms.">Ms.</option>
                                <option value="Mrs.">Mrs.</option>
                                <option value="Dr.">Dr.</option>
                            </select>
                        </div>

                        <div className={`${styles.inputGroup} ${styles.firstNameGroup}`}>
                            <label htmlFor="customer-first-name">
                                First Name <span className={styles.req}>*</span>
                            </label>
                            <input
                                id="customer-first-name"
                                type="text"
                                name="firstName"
                                value={formData.firstName}
                                onChange={handleChange}
                                className={styles.input}
                                placeholder="e.g. Aarav"
                                autoComplete="off"
                                required
                            />
                        </div>

                        <div className={`${styles.inputGroup} ${styles.lastNameGroup}`}>
                            <label htmlFor="customer-last-name">Last Name</label>
                            <input
                                id="customer-last-name"
                                type="text"
                                name="lastName"
                                value={formData.lastName}
                                onChange={handleChange}
                                className={styles.input}
                                placeholder="e.g. Sharma"
                                autoComplete="off"
                            />
                        </div>
                    </div>

                    <div className={styles.gridRow}>
                        <div className={styles.inputGroup}>
                            <label htmlFor="customer-phone">
                                Mobile Number <span className={styles.req}>*</span>
                            </label>
                            <div className={styles.inputWithIcon}>
                                <Phone size={15} className={styles.fieldIcon} />
                                <input
                                    id="customer-phone"
                                    type="tel"
                                    name="phone"
                                    value={formData.phone}
                                    onChange={handleChange}
                                    className={styles.inputBorderless}
                                    placeholder="10-digit mobile"
                                    inputMode="numeric"
                                    maxLength={10}
                                    autoComplete="off"
                                    required
                                />
                            </div>
                        </div>

                        <div className={styles.inputGroup}>
                            <label htmlFor="customer-email">Email ID</label>
                            <div className={styles.inputWithIcon}>
                                <Mail size={15} className={styles.fieldIcon} />
                                <input
                                    id="customer-email"
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    className={styles.inputBorderless}
                                    placeholder="client@email.com"
                                    autoComplete="off"
                                />
                            </div>
                        </div>
                    </div>

                    <div className={styles.inputGroup}>
                        <label htmlFor="customer-gender">Gender</label>
                        <select id="customer-gender" name="gender" value={formData.gender} onChange={handleChange} className={styles.input}>
                            <option value="Male">Male</option>
                            <option value="Female">Female</option>
                            <option value="Other">Other</option>
                            <option value="Unspecified">Prefer not to say</option>
                        </select>
                    </div>

                    <div className={styles.sectionTitle}>Important Dates & Notes</div>

                    <div className={styles.gridRow}>
                        <div className={styles.inputGroup}>
                            <label htmlFor="customer-dob">Date of Birth</label>
                            <div className={styles.inputWithIcon}>
                                <CalendarDays size={15} className={styles.fieldIcon} />
                                <input id="customer-dob" type="date" name="dob" value={formData.dob} onChange={handleChange} className={styles.inputBorderless} />
                            </div>
                        </div>

                        <div className={styles.inputGroup}>
                            <label htmlFor="customer-anniversary">Anniversary</label>
                            <div className={styles.inputWithIcon}>
                                <CalendarDays size={15} className={styles.fieldIcon} />
                                <input id="customer-anniversary" type="date" name="anniversary" value={formData.anniversary} onChange={handleChange} className={styles.inputBorderless} />
                            </div>
                        </div>
                    </div>

                    <div className={styles.inputGroup}>
                        <label htmlFor="customer-notes">Internal Notes</label>
                        <div className={styles.textareaWithIcon}>
                            <BookOpen size={15} className={styles.areaIcon} />
                            <textarea
                                id="customer-notes"
                                name="notes"
                                rows={4}
                                value={formData.notes}
                                onChange={handleChange}
                                className={styles.textareaBorderless}
                                placeholder="Allergies, preferences, or specific instructions..."
                            />
                        </div>
                    </div>
                </form>

                <div className={styles.drawerFooter}>
                    <button type="button" onClick={onClose} className={styles.cancelBtn} disabled={isSaving}>
                        Cancel
                    </button>
                    <button type="submit" form="addCustomerForm" className={styles.saveBtn} disabled={isSaving}>
                        {isSaving ? 'Saving...' : 'Save Customer'}
                    </button>
                </div>
            </section>
        </div>,
        document.body
    );
};

export default AddCustomerForm;